import React, { useState } from 'react';
import { motion, Variants } from 'framer-motion';
import { ArrowLeft, Phone, Mail, MapPin, ChevronDown } from 'lucide-react';
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] as any } }
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export default function Home() {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const services = [
    { title: 'תכנון אדריכלי', description: 'תכנון מבנים חדשים ושיפוצים מקיפים, תוך שילוב חזון אדריכלי עם דרישות תכנון ובנייה.' },
    { title: 'עיצוב פנים', description: 'יצירת חללים המשלבים אסתטיקה עם פונקציונליות, תוך התאמה מדויקת לאורח החיים של הלקוח.' },
    { title: 'הום סטיילינג', description: 'הלבשת הבית באמצעות בחירת ריהוט, חומרים, צבעים ואביזרים, ליצירת חלל הרמוני, מוקפד ובעל אופי ייחודי.' },
    { title: 'שירותי ייעוץ', description: 'ייעוץ אדריכלי ותכנוני בשלבי קבלת החלטות, בדיקת היתכנות, פרוגרמה והתאמת התכנון לצרכים ולתקציב.' },
  ];

  const values = [
    { title: 'ניסיון מוכח', description: 'למעלה מ-8 שנות ניסיון בפרויקטים מגוונים' },
    { title: 'חשיבה פונקציונלית ואסתטית', description: 'איזון מושלם בין יופי לשימושיות' },
    { title: 'עבודה מול יזמים וקבלנים', description: 'שפה משותפת עם כל הגורמים בשטח' },
    { title: 'שקיפות ותהליך מסודר', description: 'תקשורת ברורה ולוחות זמנים מדויקים' },
    { title: 'תשומת לב לפרטים', description: 'כל פרט מתוכנן ומבוצע בקפידה' }
  ];

  const processSteps = [
    { number: '01', title: 'פגישת היכרות', description: 'הבנת הצרכים, החזון והתקציב' },
    { number: '02', title: 'קונספט ותכנון', description: 'פיתוח רעיון עיצובי ותוכנית ראשונית' },
    { number: '03', title: 'תוכניות עבודה', description: 'הכנת תוכניות מפורטות ופרטי ביצוע' },
    { number: '04', title: 'ליווי וביצוע', description: 'פיקוח צמוד עד למסירה מושלמת' }
  ];

  const testimonials = [
    { text: 'הסטודיו הפך את החזון שלנו למציאות. התהליך היה מקצועי, יצירתי ומדויק. התוצאה עלתה על כל הציפיות.', author: 'משפחת סיף', project: 'בית פרטי ביאנוח' },
    { text: 'עבודה ברמה אחרת. תשומת הלב לכל פרט, ההקשבה לצרכים שלנו והמקצועיות - הכל ברמה הגבוהה ביותר.', author: 'רונית ואלון', project: 'חיפה' },
    { text: 'שיתוף פעולה מעולה מהתכנון ועד הביצוע. ממליצים בחום לכל מי שמחפש תכנון אדריכלי ועיצוב פנים ברמה גבוהה.', author: 'משפחת עאמר', project: 'שיפוץ בית פרטי' }
  ];

  const projects = [
    { title: 'בית פרטי', location: 'חיפה', type: 'דירת יוקרה', image: '/assets/images/OH - Kit.jpg' },
    { title: 'חדר שינה', location: 'גוליס', type: 'בית פרטי', image: '/assets/images/AW - MBR.jpg' },
    { title: 'משרדי דפוס', location: 'גוליס', type: 'מסחרי', image: '/assets/images/Graphco.jpg' },
    { title: 'בית פרטי', location: 'יאנוח', type: 'דירת יוקרה', image: '/assets/images/MM.jpg' },
    { title: 'בית פרטי', location: 'יאנוח', type: 'בית פרטי', image: '/assets/images/RM - Int.jpg' },
    { title: 'בית פרטי', location: 'גוליס', type: 'דירת יוקרה', image: '/assets/images/RS - Ext.jpg' },
    { title: 'פנטהאוז', location: 'עילבון', type: 'בית פרטי', image: '/assets/images/AM - Pent.jpg' },
    { title: 'מרפאה', location: 'כפר יאסיף', type: 'קליניקה', image: '/assets/images/KB - Clinic.jpg' },
    { title: 'בית פרטי', location: 'קיסריה', type: 'דירת יוקרה', image: '/assets/images/AT - Pool.jpg' },
  ];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // For Netlify forms, we let the form submit naturally
    // The form will be processed by Netlify
    console.log('Form submitted:', formData);
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToProjects = () => {
    document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div dir="rtl" className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 right-0 left-0 z-50 bg-white/90 backdrop-blur-md border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <div className="text-2xl font-light tracking-[0.2em] text-stone-800">
              NADA<span className="font-medium">SALAMEH</span>
            </div>
            <div className="hidden md:flex items-center gap-10 text-sm tracking-wide text-stone-600">
              <a href="#about" className="hover:text-stone-900 transition-colors">אודות</a>
              <a href="#services" className="hover:text-stone-900 transition-colors">שירותים</a>
              <a href="#projects" className="hover:text-stone-900 transition-colors">פרויקטים</a>
              <a href="#contact" className="hover:text-stone-900 transition-colors">צור קשר</a>
            </div>
            <Button
              onClick={scrollToContact}
              className="bg-stone-900 hover:bg-stone-800 text-white text-sm tracking-wide px-6 h-11"
            >
              תיאום פגישה
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center pt-20 bg-gradient-to-b from-[#F5F3EF] to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-4xl"
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="text-sm tracking-[0.3em] text-stone-500 uppercase">סטודיו לאדריכלות ועיצוב פנים</span>
            </motion.div>
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl lg:text-7xl font-light text-stone-900 leading-[1.1] mb-8"
            >
              עיצוב שמשנה את
              <br />
              <span className="font-medium">חווית המגורים</span>
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-lg md:text-xl text-stone-600 leading-relaxed max-w-2xl mb-12"
            >
              יוצרים מרחבים ייחודיים המשלבים חזון אדריכלי, עיצוב מדויק ותשומת לב לכל פרט.
              מלווים אתכם מהרעיון הראשון ועד הרגע בו תיכנסו לבית החדש.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-stone-900 hover:bg-stone-800 text-white text-base tracking-wide px-8 h-14 group"
              >
                לתיאום פגישת היכרות
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={scrollToProjects}
                variant="outline"
                size="lg"
                className="border-stone-300 text-stone-700 hover:bg-stone-50 text-base tracking-wide px-8 h-14"
              >
                לצפייה בפרויקטים
              </Button>
            </motion.div>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 hidden lg:block"
        >
          <ChevronDown className="w-6 h-6 text-stone-400 animate-bounce" />
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeInUp}>
              <span className="text-sm tracking-[0.3em] text-stone-500 uppercase mb-4 block">אודות הסטודיו</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-stone-900 leading-tight mb-8">
                פילוסופיית
                <br />
                <span className="font-medium">העיצוב שלנו</span>
              </h2>
              <div className="space-y-6 text-stone-600 leading-relaxed text-lg">
                <p>
                  אנו מאמינים שאדריכלות ועיצוב פנים הם שני צדדים של אותו מטבע.
                  בסטודיו שלנו, התכנון האדריכלי והעיצוב הפנימי נולדים יחד — כשפה אחת,
                  רציפה ומדויקת, שמספרת את סיפור הבית.
                </p>
                <p>
                  כל פרויקט מתחיל בהקשבה עמוקה ללקוח: להבנת אורח החיים, הצרכים והשאיפות.
                  מתוך כך אנו יוצרים מרחבים על־זמניים, המשלבים אסתטיקה, פונקציונליות
                  ודיוק תכנוני — חללים שנראים טוב, ומרגישים נכון לאורך זמן.
                </p>
              </div>

            </motion.div>
            <motion.div
              variants={fadeInUp}
              className="relative"
            >
              <div className=" rounded-sm overflow-hidden">
                <img 
                  src="/assets/images/profile.jpg" 
                  alt="Architecture design"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-[#F5F3EF] rounded-sm -z-10"></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 lg:py-32 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16 lg:mb-20">
              <span className="text-sm tracking-[0.3em] text-stone-500 uppercase mb-4 block">השירותים שלנו</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-stone-900">
                תכנון <span className="font-medium">מקיף</span>
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white p-10 lg:p-12 rounded-sm hover:shadow-xl transition-shadow duration-500 group"
                >
                  <div className="text-5xl font-extralight text-stone-300 mb-6 group-hover:text-[#9A8B7A] transition-colors">
                    0{index + 1}
                  </div>
                  <h3 className="text-2xl font-medium text-stone-900 mb-4">{service.title}</h3>
                  <p className="text-stone-600 leading-relaxed">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 lg:mb-20">
              <div>
                <span className="text-sm tracking-[0.3em] text-stone-500 uppercase mb-4 block">פרויקטים נבחרים</span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-stone-900">
                  העבודות <span className="font-medium">שלנו</span>
                </h2>
              </div>
              <p className="text-stone-600 max-w-md mt-6 lg:mt-0 leading-relaxed">
                מבחר פרויקטים המייצגים את הגישה שלנו -
                שילוב של אסתטיקה, פונקציונליות ותשומת לב לכל פרט.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="group cursor-pointer"
                >
                  <div className="aspect-[4/3] rounded-sm mb-5 overflow-hidden relative">
                    <img 
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/20 transition-all duration-500"></div>
                  </div>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-stone-900 mb-1 group-hover:text-[#9A8B7A] transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-sm text-stone-500">{project.location}</p>
                    </div>
                    <span className="text-xs tracking-wider text-stone-400 uppercase bg-stone-100 px-3 py-1 rounded-full">
                      {project.type}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 lg:py-32 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16 lg:mb-20">
              <span className="text-sm tracking-[0.3em] text-stone-400 uppercase mb-4 block">למה לבחור בנו</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light">
                היתרון <span className="font-medium">שלנו</span>
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="text-center lg:text-right"
                >
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto lg:mx-0 mb-5">
                    <div className="w-2 h-2 bg-[#9A8B7A] rounded-full"></div>
                  </div>
                  <h3 className="text-lg font-medium mb-3">{value.title}</h3>
                  <p className="text-stone-400 text-sm leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16 lg:mb-20">
              <span className="text-sm tracking-[0.3em] text-stone-500 uppercase mb-4 block">תהליך העבודה</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-stone-900">
                מהרעיון <span className="font-medium">למציאות</span>
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="relative"
                >
                  <div className="text-7xl font-extralight text-stone-200 mb-4">{step.number}</div>
                  <h3 className="text-xl font-medium text-stone-900 mb-3">{step.title}</h3>
                  <p className="text-stone-600 leading-relaxed">{step.description}</p>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-0 w-16 h-px bg-stone-200"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 lg:py-32 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16 lg:mb-20">
              <span className="text-sm tracking-[0.3em] text-stone-500 uppercase mb-4 block">המלצות</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-stone-900">
                מה הלקוחות <span className="font-medium">אומרים</span>
              </h2>
            </motion.div>
            <div className="grid lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white p-10 rounded-sm"
                >
                  <div className="text-4xl text-[#9A8B7A] mb-6">"</div>
                  <p className="text-stone-700 leading-relaxed mb-8">{testimonial.text}</p>
                  <div className="border-t border-stone-100 pt-6">
                    <p className="font-medium text-stone-900">{testimonial.author}</p>
                    <p className="text-sm text-stone-500">{testimonial.project}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl lg:text-5xl font-light text-stone-900 leading-tight mb-6"
            >
              מוכנים להתחיל את
              <br />
              <span className="font-medium">הפרויקט הבא?</span>
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-lg text-stone-600 mb-10 max-w-2xl mx-auto"
            >
              נשמח להיפגש, להכיר את החזון שלכם ולתכנן יחד את הבית או המרחב המושלם עבורכם.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-stone-900 hover:bg-stone-800 text-white text-base tracking-wide px-10 h-14 group"
              >
                לתיאום פגישת היכרות
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 lg:py-32 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-16 lg:gap-24"
          >
            <motion.div variants={fadeInUp}>
              <span className="text-sm tracking-[0.3em] text-stone-500 uppercase mb-4 block">יצירת קשר</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-stone-900 mb-8">
                בואו <span className="font-medium">נדבר</span>
              </h2>
              <p className="text-stone-600 leading-relaxed mb-12 max-w-md">
                מעוניינים לשמוע עוד? השאירו פרטים ונחזור אליכם בהקדם,
                או צרו קשר ישירות באחת הדרכים הבאות.
              </p>
              <div className="space-y-6">
                <a href="tel:03-1234567" className="flex items-center gap-4 text-stone-700 hover:text-stone-900 transition-colors group">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center group-hover:bg-stone-900 transition-colors">
                    <Phone className="w-5 h-5 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-lg">054-900-8677</span>
                </a>
                <a href="mailto:nadasal.design@gmail.com" className="flex items-center gap-4 text-stone-700 hover:text-stone-900 transition-colors group">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center group-hover:bg-stone-900 transition-colors">
                    <Mail className="w-5 h-5 group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-lg">nadasal.design@gmail.com</span>
                </a>
                <div className="flex items-center gap-4 text-stone-700">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <span className="text-lg">ירכא</span>
                </div>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <form
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="bg-white p-8 lg:p-10 rounded-sm"
              >
                <input type="hidden" name="form-name" value="contact" />
                <div className="hidden">
                  <label>
                    Don't fill this out if you're human: <input name="bot-field" />
                  </label>
                </div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">שם מלא</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12 border-stone-200 focus:border-stone-400 focus:ring-stone-400"
                      placeholder="הזינו את שמכם"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">טלפון</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-12 border-stone-200 focus:border-stone-400 focus:ring-stone-400"
                      placeholder="050-000-0000"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">ספרו לנו על הפרויקט</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, message: e.target.value })}
                      className="min-h-[120px] border-stone-200 focus:border-stone-400 focus:ring-stone-400 resize-none"
                      placeholder="תארו בקצרה את הפרויקט שלכם..."
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-stone-900 hover:bg-stone-800 text-white h-12 text-base tracking-wide"
                  >
                    שליחת פנייה
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-2xl font-light tracking-[0.2em]">
              NADA<span className="font-medium">SALAMEH</span>
            </div>
            <div className="text-sm text-stone-400">
              © 2025 כל הזכויות שמורות
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}